/* eslint-disable max-lines */

import { NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { merge as observableMerge, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { noWhitespaceValidator } from '@recruitee/common';
import { Attribute, AttributeListData } from '@recruitee/http/types';

import { DropdownHeaderComponent } from '../../common/dropdown/dropdown-header/dropdown-header.component';
import { DropdownItemComponent } from '../../common/dropdown/dropdown-item/dropdown-item.component';
import { DropdownComponent } from '../../common/dropdown/dropdown.component';
import { IconColorDirective } from '../../common/icon/directives/icon-color.directive';
import { IconComponent } from '../../common/icon/icon.component';
import { HotkeysService } from '../../core/a11y/hotkeys.service';
import { KeyCode } from '../../core/core.module';
import { FormFieldComponent } from '../../forms/form-field/form-field.component';
import { InputComponent } from '../../forms/input/input.component';
import { RtStatefulComponent } from '../../utils/rt-stateful-component';
import { AttributesSelectListComponent } from '../attributes-select-list/attributes-select-list.component';
import { AttributesLoadMore, AttributesTranslationsType } from '../attributes.types';

export interface State {
  suggestions: AttributeListData;
  rawSuggestions: AttributeListData;
  attributes: Attribute[];
  displayAlreadyExists: boolean;
  highlighted: {
    id: number | string;
    index: number;
  } | null;
  createItemVisible: boolean;
  preventCreateMessageVisible: boolean;
  errors: ValidationErrors;
}

@Component({
  selector: 'rt-attributes-select',
  templateUrl: './attributes-select.component.html',
  styleUrls: ['./attributes-select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.width]': 'width',
  },
  standalone: true,
  imports: [
    AttributesSelectListComponent,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    FormFieldComponent,
    IconColorDirective,
    IconComponent,
    InputComponent,
    NgIf,
    NgTemplateOutlet,
    ReactiveFormsModule,
  ],
})
export class AttributesSelectComponent extends RtStatefulComponent<State> {
  public readonly CREATE_NEW_ITEM_ID: string = 'create_new';
  public readonly defaultControl: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    noWhitespaceValidator,
  ]);

  @Input() public canPick: boolean = false;
  @Input() public canCreate: boolean = false;
  @Input() public createDisabled: boolean = false;

  @Input() public resetOnSaveFromInput: boolean = true;
  @Input() public resetOnSaveFromSuggestion: boolean = false;

  @Input() public loading: boolean = false;
  @Input() public width: string;

  @Input() public selectFooterTemplate: TemplateRef<any>;
  @Input() public selectHeaderTemplate: TemplateRef<any>;
  @Input() public hasSearch: boolean = true;

  @Input()
  public set suggestions(suggestions: AttributeListData) {
    this._suggestions = suggestions;
  }

  public get suggestions(): AttributeListData {
    return this.state.suggestions;
  }

  @Input()
  public set showAlreadyExists(displayAlreadyExists: boolean) {
    this._showAlreadyExists = displayAlreadyExists;
  }

  public get showAlreadyExists(): boolean {
    return this.state.displayAlreadyExists;
  }

  @Input() public maxDropdownHeight: string;
  @Input() public maxDropdownWidth: string;

  @Input() public text: AttributesTranslationsType;
  @Input() public header?: string;

  @Input() public initialValue: string = '';
  @Input() public maxLength: number;

  @Output() public dispose: EventEmitter<void> = new EventEmitter();
  @Output() public save: EventEmitter<Attribute> = new EventEmitter();
  @Output() public query: EventEmitter<string> = new EventEmitter<string>();
  @Output() public loadMore = new EventEmitter<AttributesLoadMore>();

  @ViewChild('createItemTpl') public createItemTemplate: TemplateRef<any>;
  @ViewChild('input') public rtInput: InputComponent;

  @Input() public control: UntypedFormControl;

  @Output() public goToSettings = new EventEmitter<void>();

  private ngOnDestroy$ = new Subject<void>();
  private _suggestions: AttributeListData;
  private _showAlreadyExists: boolean;

  constructor(protected cdRef: ChangeDetectorRef, private hotkeys: HotkeysService) {
    super(cdRef);

    this.createState({
      highlighted: null,
      preventCreateMessageVisible: false,
      displayAlreadyExists: false,
      createItemVisible: false,
      suggestions: { isLoading: false, meta: null, rows: [] },
      rawSuggestions: { isLoading: false, meta: null, rows: [] },
      attributes: [],
      errors: [],
    });

    this.handleKeyboardNavigation();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.control) {
      this.control = this.defaultControl;
    }
    if ('showAlreadyExists' in changes) {
      this.setState(
        {
          ...this.state,
          displayAlreadyExists: changes.showAlreadyExists.currentValue,
        },
        false,
      );

      this.updateSuggestions(this.state.rawSuggestions);
    }
    if ('suggestions' in changes) {
      this.updateSuggestions(changes.suggestions.currentValue);
    }
    if ('maxLength' in changes) {
      // there is no getValidators() method
      // https://github.com/angular/angular/issues/13461
      const existingValidators = [Validators.required, noWhitespaceValidator];
      this.control.setValidators([
        ...existingValidators,
        Validators.maxLength(changes.maxLength.currentValue),
      ]);
      if (this.control.value) this.control.updateValueAndValidity();
    }
  }

  public keydownEscape($event: Event): void {
    $event.preventDefault();

    if (this.control.valid) {
      this.control.reset();
    } else {
      this.dispose.emit();
    }
  }

  public ngOnInit(): void {
    this.control.valueChanges.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.query.emit(this.normalizedControlValue);
      this.setState({
        ...this.state,
        preventCreateMessageVisible: !!this.state.preventCreateMessageVisible,
        errors: this.control.errors!,
      });
    });

    // this will emit `query` update
    if (this.control === this.defaultControl) {
      this.control.setValue(this.initialValue);
    }
    this.control.markAsPristine();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onSaveAttribute(attr: Attribute, saveOrigin: 'suggestion' | 'input'): void {
    this.saveAttribute(attr, saveOrigin);
    if (this.rtInput) {
      this.rtInput.setFocus();
    }
  }

  public onLoadMore(): void {
    const { limit, page } = this.suggestions.meta!;
    this.loadMore.emit({
      limit,
      page: page + 1,
      query: this.control.value,
    });
  }

  private saveAttribute(attr: Attribute, saveOrigin: 'suggestion' | 'input'): void {
    if (this.showAlreadyExists && saveOrigin === 'input') {
      return;
    }

    if (
      (saveOrigin === 'suggestion' && this.resetOnSaveFromSuggestion) ||
      (saveOrigin === 'input' && this.resetOnSaveFromInput)
    ) {
      this.control.reset('', { emitEvent: true, onlySelf: true });
    }

    this.setState({
      ...this.state,
    });

    this.save.emit(attr);
  }

  private get normalizedControlValue(): string {
    return this.control.value === null ? '' : this.control.value.trim().toLowerCase();
  }

  private get highlighted(): Attribute {
    return this.suggestions.rows.find(attr => attr.id === this.state.highlighted!.id)!;
  }

  private handleKeyboardNavigation(): void {
    this.hotkeys
      .shortcut([KeyCode.Enter], false)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        if (!this.canPick) {
          return;
        }

        if (!this.canCreate) {
          if (this.suggestions.rows.length > 0 && this.control.valid && !this.state.highlighted) {
            this.setState({
              ...this.state,
              preventCreateMessageVisible: true,
            });
          } else if (this.state.highlighted) {
            this.saveAttribute(this.highlighted, 'suggestion');
          }
        } else {
          if (this.state.highlighted) {
            this.saveAttribute(this.highlighted, 'suggestion');
          } else if (this.control.valid) {
            this.saveAttribute({ value: this.control.value }, 'input');
          }
        }
      });

    observableMerge<Array<-1 | 1>>(
      this.hotkeys.shortcut([KeyCode.UpArrow], false).pipe(map(() => -1)),
      this.hotkeys.shortcut([KeyCode.DownArrow], false).pipe(map(() => 1)),
    )
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(delta => this.moveHighlight(delta));
  }

  // eslint-disable-next-line max-lines-per-function
  private moveHighlight(delta: 1 | -1 | 0, runCd: boolean = true): void {
    if (this.suggestions.rows.length === 0) {
      return;
    }

    if (!this.state.highlighted) {
      this.setState(
        {
          ...this.state,
          highlighted: {
            id: (delta === 1
              ? this.suggestions.rows[0]
              : this.suggestions.rows[this.suggestions.rows.length - 1]
            ).id!,
            index: delta === 1 ? 0 : this.suggestions.rows.length - 1,
          },
        },
        runCd,
      );

      return;
    }

    const nextIndex = this.state.highlighted.index + delta;
    const nextHighlighted = this.suggestions.rows[nextIndex]
      ? {
          id: this.suggestions.rows[nextIndex].id!,
          index: nextIndex,
        }
      : delta === 1
      ? {
          id: this.suggestions.rows[0].id!,
          index: 0,
        }
      : {
          id: this.suggestions.rows[this.suggestions.rows.length - 1].id!,
          index: this.suggestions.rows.length - 1,
        };

    this.setState(
      {
        ...this.state,
        highlighted: nextHighlighted,
      },
      runCd,
    );
  }

  // eslint-disable-next-line max-lines-per-function
  private updateSuggestions(suggestions: AttributeListData): void {
    let nextHighlighted = null;
    let createItemVisible = false;
    let preventCreateMessageVisible = this.state.preventCreateMessageVisible;

    const normalizedControlValue = this.normalizedControlValue;

    // reset highlight if there is no results
    if (suggestions.rows.length === 0) {
      this.setState(
        {
          ...this.state,
          highlighted: null,
        },
        false,
      );
    }

    if (this.canCreate && suggestions.rows.length > 0 && this.control.valid) {
      const inputValueExactMatch = this.findAttributeByInputValue(
        normalizedControlValue,
        suggestions.rows,
      );

      if (!inputValueExactMatch) {
        createItemVisible = true;
      }
    }

    const nextSuggestions = {
      ...suggestions,
      rows:
        createItemVisible && !this.showAlreadyExists
          ? [...suggestions.rows, { id: this.CREATE_NEW_ITEM_ID, value: normalizedControlValue }]
          : suggestions.rows,
    };

    if (nextSuggestions.rows.length === 1) {
      if (this.canCreate || suggestions.rows[0].value === normalizedControlValue) {
        nextHighlighted = {
          id: suggestions.rows[0].id!,
          index: 0,
        };
      } else {
        preventCreateMessageVisible = true;
      }
    }

    if (nextSuggestions.rows.length === 0) {
      preventCreateMessageVisible = false;
    }

    this.setState(
      {
        ...this.state,
        preventCreateMessageVisible,
        highlighted: nextHighlighted,
        createItemVisible,
        rawSuggestions: suggestions,
        suggestions: nextSuggestions,
      },
      false,
    );
  }

  private findAttributeByInputValue(
    inputValue: string,
    attributes: Attribute[],
  ): Attribute | undefined {
    return attributes.find(a => a.value.toLowerCase() === inputValue.toLowerCase());
  }
}
